import { getChainIcon, getChainType, isEVMChain } from '@particle-network/connector-core';
import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useMemo, useRef, useState } from 'react';
import CheckIcon from '../../assets/icons/CheckIcon';
import { useChainId } from '../../hooks/useChainId';
import { useChains } from '../../hooks/useChains';
import useClickAway from '../../hooks/useClickAway';
import useConnect from '../../hooks/useConnect';
import { useConnections } from '../../hooks/useConnections';
import { useConnectors } from '../../hooks/useConnectors';
import { useSwitchAccount } from '../../hooks/useSwitchAccount';
import { useSwitchChain } from '../../hooks/useSwitchChain';
import { useWalletConnections } from '../../hooks/useWalletConnections';
import { getChainDisplayName } from '../../utils/chains';
import CircleSpinner from '../circleSpinner';
import {
  StyleChainItem,
  StyleChainName,
  StyleCheckIcon,
  StyleDropdownContent,
  StyleDropdownWrapper,
  StyleHoverBg,
} from './styles';

const ChainDropdown = ({
  visible,
  setVisible,
  clickAwayElements,
  maxHeight,
  supportChain = true,
  top,
}: {
  visible: boolean;
  setVisible: (visible: boolean) => void;
  clickAwayElements: React.RefObject<HTMLDivElement>[];
  maxHeight?: number;
  supportChain?: boolean;
  top?: number;
}) => {
  const chainList = useChains();
  const chainId = useChainId();
  const { switchChainAsync, status: switchStatus } = useSwitchChain();
  const [walletConnection] = useWalletConnections();
  const { switchAccountAsync } = useSwitchAccount();
  const connectors = useConnectors();
  const { connectAsync } = useConnect();
  const connections = useConnections();

  const [hoverIndex, setHoverIndex] = useState(-1);
  const [targetChainId, setTargetChainId] = useState<number>();

  const dropdownRef = useRef<HTMLDivElement>(null);
  const timer = useRef<NodeJS.Timeout>(undefined);
  const dropdownWrapperRef = useRef<HTMLDivElement>(null);
  const selectedItemRef = useRef<HTMLDivElement>(null);

  const isParticle = useMemo(() => {
    return Boolean(walletConnection && walletConnection.walletProps.connector.walletConnectorType === 'particleAuth');
  }, [walletConnection]);

  const currentChainId = useMemo(() => {
    return supportChain ? chainId : -1;
  }, [chainId, supportChain]);

  /**
   * Switch success
   */
  const switchSuccess = useMemo(() => {
    return targetChainId === currentChainId;
  }, [targetChainId, currentChainId]);

  const currentChainType = useMemo(() => {
    if (chainList && chainList?.length && currentChainId) {
      const chain = chainList.find((item) => item.id === currentChainId);
      if (chain) {
        return getChainType(chain);
      }
      return '';
    } else {
      return '';
    }
  }, [chainList, currentChainId]);

  useEffect(() => {
    if (switchSuccess && visible) {
      setVisible(false);
      setTargetChainId(undefined);
    }
  }, [switchSuccess, visible, setVisible]);

  useEffect(() => {
    console.log('🚀 ~ currentChainId:', currentChainId);
  }, [currentChainId]);

  useClickAway(() => {
    setVisible(false);
  }, [dropdownRef, ...clickAwayElements]);

  useEffect(() => {
    setTimeout(() => {
      if (visible && dropdownWrapperRef.current && selectedItemRef.current) {
        const dropdownWrapper = dropdownWrapperRef.current;
        const selectedItem = selectedItemRef.current;
        const wrapperHeight = dropdownWrapper.clientHeight;
        const itemHeight = selectedItem.clientHeight;
        const itemTop = selectedItem.offsetTop;
        const scrollTop = itemTop - wrapperHeight / 2 + itemHeight / 2;
        const currentScrollTop = dropdownWrapper.scrollTop;
        if (itemTop < currentScrollTop || itemTop + itemHeight > currentScrollTop + wrapperHeight) {
          dropdownWrapper.scrollTop = scrollTop;
        }
      }
    }, 50);
  }, [visible, currentChainId]);

  return (
    <StyleDropdownContent ref={dropdownRef} top={top || 46}>
      <AnimatePresence>
        {visible && (
          <motion.div
            style={{
              overflow: 'hidden',
            }}
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
          >
            <StyleDropdownWrapper
              ref={dropdownWrapperRef}
              style={{
                maxHeight: maxHeight || 320,
              }}
            >
              <StyleHoverBg index={hoverIndex} />
              {chainList.map((chain) => (
                <StyleChainItem
                  key={chain.id}
                  selected={currentChainId === chain.id}
                  ref={currentChainId === chain.id ? selectedItemRef : null}
                  onClick={async () => {
                    try {
                      if (chain.id === currentChainId) return;
                      setTargetChainId(chain.id);
                      if (isParticle && getChainType(chain) !== currentChainType) {
                        const id = isEVMChain(chain) ? 'particleEVM' : 'particleSolana';
                        const connector = connectors.find((item) => item.id === id)!;
                        const isAuthorized = await connector.isAuthorized();
                        if (!isAuthorized) {
                          const provider = isEVMChain(chain) ? window.particle?.ethereum : window.particle?.solana;
                          await provider!.connect();
                          await connectAsync({
                            connector,
                            chainId: chain.id,
                          });
                          return;
                        } else {
                          // 如果是emial/phone登录的，且已经生成了钱包，如果没有连接的话需要先连接
                          if (connections.some((connection) => connection.connector.id === id)) {
                            await switchAccountAsync({ connector });
                          } else {
                            await connectAsync({
                              connector,
                              chainId: chain.id,
                            });
                            return;
                          }
                        }
                      }
                      console.log('Switch chain:', chain.id);
                      await switchChainAsync({
                        chainId: chain.id,
                      });
                    } catch (error) {
                      console.log('Switch chain error:', error);
                      setVisible(false);
                      setTargetChainId(undefined);
                    }
                  }}
                  onMouseEnter={() => {
                    clearTimeout(timer.current);
                    setHoverIndex(chainList.findIndex((item) => item.id === chain.id));
                  }}
                  onMouseLeave={() => {
                    timer.current = setTimeout(() => {
                      setHoverIndex(-1);
                    }, 100);
                  }}
                >
                  <div
                    style={{
                      marginRight: '10px',
                    }}
                  >
                    <CircleSpinner
                      inset={0}
                      width={20}
                      height={20}
                      connecting={Boolean(!switchSuccess && switchStatus === 'loading' && chain.id === targetChainId)}
                      logo={<img src={getChainIcon(chain)} alt={chain.name} />}
                    />
                  </div>
                  <StyleChainName>{getChainDisplayName(chain)}</StyleChainName>
                  <StyleCheckIcon>{currentChainId === chain.id && <CheckIcon />}</StyleCheckIcon>
                </StyleChainItem>
              ))}
            </StyleDropdownWrapper>
          </motion.div>
        )}
      </AnimatePresence>
    </StyleDropdownContent>
  );
};

export default ChainDropdown;
